import {
    Component,
    Input,
    OnInit,
    OnDestroy,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
    CellFormatterService,
    TableState,
    DataResponse,
    ColumnsState,
    TableColumnDef,
} from '@common/datatable';
import {
    BaseFacet,
    BaseFacetService,
    IFacet
} from '../common/facet';
import { filterToDate } from '../common/util';

import { EnumerationService } from '../enumerations/enumeration.service';
import { TranslationService } from '../services/translation.service';
import { VocabularyService } from '../vocabularies/vocabulary.service';
import { WorkspaceFilterService } from '../services/workspace-filter.service';
import { WsFilterEvent } from '../services/ws-filter-event';

import { JobFilterComponent } from './job-filter.component';
import { JobLogicService } from './job-logic.service';
import { JobService } from './job.service';
import { JobTableOptions } from './job-table-options';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { Entity, Job } from '../common/types';
import { FeatureFlagService } from '../services/feature-flags.service';
import { arrowClockwise, brokenChain, chain, magnifier, viceVersaArrowsHorizontal } from '@icons';


@Component({
    selector: 'job-facet',
    templateUrl: './job-facet.component.html',
    providers: BaseFacet.BASE_COMPONENT_PROVIDERS
})
export class JobFacetComponent extends BaseFacet<Entity<Job>>
    implements OnInit, OnDestroy {
    @Input() facetId: string;
    @Input() facet: IFacet;

    readonly icons = { arrowClockwise, brokenChain, chain, magnifier, viceVersaArrowsHorizontal };

    componentName = 'job';

    jobTableOptions: JobTableOptions;

    readonly COMPONENT_LOG_TAG = 'job-facet';

    _subs: Subscription[] = [];

    dataTableColumns: BehaviorSubject<TableColumnDef[]>;
    dataTableColumns$: Observable<TableColumnDef[]>;

    constructor(
        private baseFacetService: BaseFacetService,
        private cellFormatterService: CellFormatterService,
        private enumerationService: EnumerationService,
        private jobLogicService: JobLogicService,
        private jobService: JobService,
        private translationService: TranslationService,
        private vocabularyService: VocabularyService,
        private modalService: NgbModal,
        workspaceFilterService: WorkspaceFilterService,
        private featureFlagService: FeatureFlagService
    ) {
        super(
            baseFacetService,
            workspaceFilterService
        );

        this.jobTableOptions = new JobTableOptions(
            this.cellFormatterService,
            this.translationService,
            this.featureFlagService
        );

        this.dataTableColumns = new BehaviorSubject(this.jobTableOptions.options.columns);
        this.dataTableColumns$ = this.dataTableColumns.asObservable();

        this.dataService = {
            run: (tableState: TableState) => {
                return this.loadJobsList(tableState);
            }
        };

        if (this.jobService && this.jobService.changeStudies1ViewMethodCalled$) {
            this._subs.push(this.jobService.changeStudies1ViewMethodCalled$.subscribe(
                () => {
                    this.changeView(this.LIST_VIEW);
                }
            ));
        }        
    }

    // lifecycle
    ngOnInit() {
        super.ngOnInit();
        this.supportedWorkspaceFilters = ['animal-filter'];

        this.initialize();

        this.createPaginator();
    }

    ngOnDestroy() {
        for (const s of this._subs) {
            s.unsubscribe();
        }
    }

    initialize() {
        this.restoreFilterState();
        this.changeView(this.LIST_VIEW);
    }

    refreshData() {
        this.initialize();
        this.reloadTable();
    }

    restoreFilterState() {

        // process any grid filters
        if (this.facet && this.facet.GridFilter) {
            try {
                this.filter = JSON.parse(this.facet.GridFilter);
            } catch (err) {
                console.error(err);
            }

            if (this.filter) {
                // convert facet-specific data
                this.filter.ProjectImplantDateStart = filterToDate(this.filter.ProjectImplantDateStart);
                this.filter.ProjectImplantDateEnd = filterToDate(this.filter.ProjectImplantDateEnd);

                this.filter.ImplantedDateStart = filterToDate(this.filter.ImplantedDateStart);
                this.filter.ImplantedDateEnd = filterToDate(this.filter.ImplantedDateEnd);

                this.filter.DateCreatedStart = filterToDate(this.filter.DateCreatedStart);
                this.filter.DateCreatedEnd = filterToDate(this.filter.DateCreatedEnd);

                this.filter.ProjectedStartDateStart = filterToDate(this.filter.ProjectedStartDateStart);
                this.filter.ProjectedStartDateEnd = filterToDate(this.filter.ProjectedStartDateEnd);

                this.filter.DateStartedStart = filterToDate(this.filter.DateStartedStart);
                this.filter.DateStartedEnd = filterToDate(this.filter.DateStartedEnd);

                this.filter.DateEndedStart = filterToDate(this.filter.DateEndedStart);
                this.filter.DateEndedEnd = filterToDate(this.filter.DateEndedEnd);
            } else {
                this.filter = {};
            }
        }
    }

    async loadJobsList(tableState: TableState): Promise<DataResponse> {
        this.tableState = tableState;

        const page = tableState.pageNumber || 0;
        const pageSize = tableState.pageSize || 50;
        const sort = tableState.sort || 'DateCreated DESC';

        this.setLoadingState(tableState.loadingMessage);

        try {
            const response = await this.jobService.getJobsWithAllDependencies({
                page,
                size: pageSize,
                sort,
                filter: this.getActiveFilter()
            });

            const visibleColumns = this.getVisibleColumns(this.jobTableOptions.options);
            await this.jobService.ensureVisibleColumnsDataLoaded(response.results, visibleColumns);

            this.stopLoading();

            this.data = response.results as Entity<Job>[];
            this.totalCount = response.inlineCount;
            this.updatePageState();

            return {
                results: this.data,
                inlineCount: this.totalCount
            };
        } finally {
            this.stopLoading();
        }
    }

    addItemClick() {
        this.setLoadingState();
        this.jobLogicService.createNewJob().then((job) => {
            this.stopLoading();
            this.itemToEdit = job;
            this.changeView(this.DETAIL_VIEW);
        }).catch((error) => {
            this.loading = false;
            this.loggingService.logError("An unexpected error occurred. Please try again", error, this.componentName, true);
        });
    }

    modelCopied(itemCopied: Entity<Job>) {
        this.itemToEdit = itemCopied;

        const showToast = true;
        this.loggingService.logSuccess(
            this.translationService.translate('Job') + ' copied',
            null,
            this.COMPONENT_LOG_TAG,
            showToast
        );
        this.changeView(this.DETAIL_VIEW);
    }

    openFilter() {
        const ref = this.modalService.open(JobFilterComponent, { size: 'lg' });
        const component = ref.componentInstance as JobFilterComponent;
        component.filter = this.filter;
        this._subs.push(component.onFilter.subscribe((filter: any) => {
            this.filter = filter;
            this.runFilter();
        }));
    }

    selectedRowsChange(rows: any[]) {
        if (this.workspaceFilterActive) {
            this.filterWorkspaceByJob();
        }
    }

    filterWorkspaceByJob() {
        let jobKeys = this.selectedRows.map((job) => job.C_Job_key);
        const maxJobs = 10;

        if (jobKeys.length > maxJobs) {
            // Limit number of IDs to avoid errors b/c of GET URL length.
            // TODO Use POST for filtering facets
            //   http://breeze.github.io/doc-breeze-labs/ajax-post.html
            jobKeys = jobKeys.slice(0, maxJobs);

            const showToast = true;
            this.loggingService.logWarning(
                'Limiting workspace filter to ' + maxJobs + ' ' +
                this.translationService.translate('jobs'),
                null,
                this.COMPONENT_LOG_TAG,
                showToast
            );
        }

        this.workspaceFilterService.filterWorkspace(this.facetId, 'job-filter', jobKeys);
    }

    onWorkspaceFilterChange(wsFilterEvent: WsFilterEvent) {
        const oldFilterSupported = this.workspaceFilterSupported(wsFilterEvent.oldFilterKind);
        const newFilterSupported = this.workspaceFilterSupported(wsFilterEvent.filterKind);
        if (!this.ignoreWorkspaceFilter && (oldFilterSupported || newFilterSupported)) {
            this.reloadTable();
        }
    }

    async selectedColumnsChange({ visible }: ColumnsState) {
        try {
            this.facetLoadingState.changeLoadingState(true);
            await this.jobService.ensureVisibleColumnsDataLoaded(this.data, visible);
        } finally {
            this.facetLoadingState.changeLoadingState(false);
        }
    }
}
